import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, euro } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { PlugZap } from "lucide-react";

const when = (v) => (v ? new Date(v).toLocaleString("it-IT") : "");

const Products = ({ s }) => (
  <div className="space-y-1.5" data-testid="uno-products">
    <p className="text-sm font-medium">Offerte del portale</p>
    <p className="text-xs text-muted-foreground">{s.products.length ? `Diventano offerte Markoo da sole e si aggiornano ogni giorno (ultimo aggiornamento ${when(s.products_at)}). Dati, minuti e SMS li completi in «Operatori mobili».` : "Premi «Prova collegamento» per leggere le offerte del portale."}</p>
    {s.products.map((p) => <div key={p.id} className="flex items-center justify-between gap-2 text-sm border-b border-border py-1" data-testid={`uno-product-${p.id}`}>
      <span className="min-w-0 flex-1 truncate">{p.name}</span>
      <span className="shrink-0 text-xs text-muted-foreground">{p.monthly_price != null ? `${euro(p.monthly_price)}/mese` : "canone non letto"} · attivazione {euro(p.activation_price)}{p.mnp_ok ? "" : " · no portabilità"}</span>
    </div>)}
  </div>
);

const Calls = ({ calls }) => (
  <div className="space-y-1" data-testid="uno-calls">
    <p className="text-sm font-medium">Ultimi accessi al portale</p>
    {!calls.length && <p className="text-xs text-muted-foreground">Nessun accesso finora.</p>}
    {calls.map((c, i) => <div key={i} className="grid grid-cols-[auto_minmax(0,1fr)_auto] gap-2 text-xs border-b border-border py-1" data-testid={`uno-call-${i}`}>
      <span className={c.ok ? "text-teal-600" : "text-destructive"}>{c.ok ? "OK" : "Errore"}</span>
      <span className="truncate"><span className="font-mono">{c.path}</span>{c.reason ? ` · ${c.reason}` : ""}</span>
      <span className="text-muted-foreground">{when(c.at)}</span>
    </div>)}
  </div>
);

const Facts = ({ s }) => {
  const m = s.monitor || {};
  const rows = [
    ["E-wallet Uno Mobile (per le ricariche)", s.ewallet != null ? `${euro(s.ewallet)} · letto il ${when(s.ewallet_at)}` : "da leggere"],
    ["Ultima prova", s.test.at ? `${s.test.ok ? "riuscita" : `non riuscita: ${s.test.reason}`} · ${when(s.test.at)}` : "mai eseguita"],
    ["Elenco province e comuni", s.geo_ready ? "pronto" : "si prepara dopo la prima prova"],
    ["Controllo stati (ogni ora)", m.at ? (m.skipped ? `${when(m.at)}: saltato, ${m.skipped}` : `${when(m.at)}: ${m.checked ?? 0} controllate · ${m.completed ?? 0} completate · ${m.errors ?? 0} con errore`) : "nessun controllo finora"],
  ];
  return <div className="rounded-lg border border-border p-3 space-y-1" data-testid="uno-facts">
    {rows.map(([k, v]) => <div key={k} className="flex justify-between gap-3 text-xs"><span className="text-muted-foreground">{k}</span><span className="text-right">{v}</span></div>)}
  </div>;
};

export const UnoMobilePanel = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/unomobile").then(({ data }) => setS(data)).catch(() => toast.error("Stato del collegamento Uno Mobile non disponibile"));
  useEffect(() => { load(); }, []);
  const run = async (fn) => {
    setBusy(true);
    try { toast.success(await fn()); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Operazione non riuscita"); } finally { setBusy(false); load(); }
  };
  const toggle = (v) => run(() => api.put("/admin/unomobile", { enabled: v }).then(() => (v ? "Collegamento acceso" : "Collegamento spento")));
  const test = () => run(() => api.post("/admin/unomobile/test").then(({ data }) => { window.dispatchEvent(new Event("operators-changed")); return `Collegamento riuscito: ${data.offers} offerte aggiornate`; }));
  if (!s) return <p className="text-sm text-muted-foreground">Carico il collegamento…</p>;
  return <div className="rounded-lg border border-border p-4 space-y-4" data-testid="uno-panel">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0"><p className="font-medium flex items-center gap-2"><PlugZap className="w-4 h-4 text-primary" />Collegamento al portale Uno Mobile</p>
        <p className="text-xs text-muted-foreground mt-0.5" data-testid="uno-config-text">{s.configured ? "Utente e password del portale presenti." : `Mancano utente e password del portale (${s.missing.join(", ")}).`}</p></div>
      <Badge variant={s.on ? "default" : "outline"} data-testid="uno-status">{s.on ? "Acceso" : s.configured ? "Spento" : "Da configurare"}</Badge>
    </div>
    <label className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
      <span><span className="text-sm font-medium block">Usa il portale Uno Mobile</span><span className="text-xs text-muted-foreground">Attivazioni, portabilità, ricariche, info SIM e cambi promo partono da soli sul portale. Se lo spegni, le pratiche restano in coda per te.</span></span>
      <Switch checked={s.enabled} disabled={busy || (!s.configured && !s.enabled)} onCheckedChange={toggle} data-testid="uno-enabled" />
    </label>
    <div className="flex flex-wrap gap-2">
      <Button variant="outline" size="sm" disabled={!s.configured || busy} onClick={test} data-testid="uno-test">Prova collegamento e aggiorna le offerte</Button>
      <Button variant="outline" size="sm" disabled={!s.on || busy} onClick={() => run(() => api.post("/admin/unomobile/monitor").then(() => "Controllo avviato: tra poco vedi il risultato"))} data-testid="uno-monitor-run">Controlla stati ora</Button>
    </div>
    <Facts s={s} />
    <Products s={s} />
    <Calls calls={s.calls} />
  </div>;
};
